import posthog from "posthog-js";
import { unregisterPushToken } from "../hooks/usePushNotifications";
import { clearLoggedInUserDataInLocalStorage } from "../config/config";
import {
  getLanguageInLocalStorage,
  setLanguageInLocalStorage,
} from "../hooks/useLanguagePercistant";
import { logoutResetLoginRegister } from "../redux/features/loginRegisterSlice";
import { clearAllClaimFiles } from "./claimFilesStorage";
import { clearDomesticHelperFiles } from "./domesticHelperFilesStorage";
import { clearHomeFiles } from "./homeFilesStorage";
import { clearSmartDeviceFiles } from "./smartDeviceFilesStorage";
import { clearTravelFiles } from "./travelFilesStorage";
import { clearTravelQuoteCache, clearHomeQuoteCache, clearBniTravelQuoteCache, cancelAllTravelQuoteRequests } from "../api/quoteServices";
import { resetRedux } from "../redux/features/resetSlice";

/**
 * Centralized logout: clears all cached user/policy/claim data from
 * Redux, localStorage and sessionStorage. Preserves the user's language
 * preference so the next visit still uses their chosen language.
 *
 * Usage: performLogout({ dispatch, navigate });
 */
export const performLogout = ({ dispatch, navigate, redirectTo = "/" } = {}) => {
  // 0) Drop the push registration FIRST. /unregister-device is JWT-authed and
  //    the token to remove is read from localStorage, so this has to happen
  //    while the session still exists — clearing storage first would leave the
  //    device registered and still receiving this user's pushes.
  //
  //    Deliberately not awaited: logout must not block on the network.
  //    unregisterPushToken reads the token and the JWT synchronously and pins
  //    the Authorization header onto the request itself, so the call still
  //    authenticates even though it lands after the clears below.
  try {
    unregisterPushToken();
  } catch (e) {}

  // 1) Capture preferences we want to keep across logout
  const lang = getLanguageInLocalStorage();

  // 2) Clear Redux: first drop the auth token from login_register, then
  //    dispatch the global reset (rootReducer preserves login_register,
  //    tools, common_api_content — but login_register is now empty).
  if (dispatch) {
    try {
      dispatch(logoutResetLoginRegister());
    } catch (e) {}
    try {
      dispatch(resetRedux());
    } catch (e) {}
  }

  // 2b) Clear in-memory file caches (uploaded docs, OCR blobs)
  try { clearAllClaimFiles(); } catch (e) {}
  try { clearDomesticHelperFiles(); } catch (e) {}
  try { clearHomeFiles(); } catch (e) {}
  try { clearSmartDeviceFiles(); } catch (e) {}
  try { clearTravelFiles(); } catch (e) {}
  try { clearTravelQuoteCache(); } catch (e) {}
  try { clearBniTravelQuoteCache(); } catch (e) {}
  try { clearHomeQuoteCache(); } catch (e) {}
  try { cancelAllTravelQuoteRequests(); } catch (e) {}

  // 3) Wipe all cached client storage — policy details, claims, OCR,
  //    quote responses, OTP tokens, IMEI cache, onboarding flags, etc.
  try {
    clearLoggedInUserDataInLocalStorage();
  } catch (e) {}
  try {
    sessionStorage.clear();
  } catch (e) {}
  try {
    localStorage.clear();
  } catch (e) {}

  // 4) Restore the language preference
  try {
    setLanguageInLocalStorage(lang || "en");
  } catch (e) {}

  // 5) Reset 3rd-party identity / analytics
  try {
    posthog.reset(true);
  } catch (e) {}

  // 6) Navigate home
  if (navigate) {
    try {
      navigate(redirectTo);
    } catch (e) {}
  }
};

export default performLogout;
